import { Component, computed, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { DefaultMira, Favorite, FavoriteStatus, MainItem, SubItem } from '@irohalab/mira-sdk-angular';
import { catchError, EMPTY, finalize, map, of, Subscription, switchMap, take, takeWhile, tap, timer } from 'rxjs';
import { DatePipe, NgClass } from '@angular/common';
import { EditReviewDialogComponent } from './edit-review-dialog/edit-review-dialog.component';
import { UIDialog, UIToast, UIToastComponent, UIToastRef } from '@irohalab/deneb-ui';
import { MyReviewComponent } from './my-review/my-review.component';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BangumiProviderService } from '../../bangumi-provider.service';
import { BgmSyncOperation, BgmSyncStatus, BgmSyncTaskState } from '../../../sdk/bangumiProvider';
import { HttpErrorResponse } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { extractMessage } from '../../utils/error.util';
import EpisodeTypeEnum = SubItem.EpisodeTypeEnum;

const BGM_SYNC_POLL_INTERVAL_MS = 3000;
const BGM_SYNC_MAX_POLLS = 240;
const ACTIVE_BGM_SYNC_STATES = new Set<BgmSyncTaskState>([
  BgmSyncTaskState.Pending,
  BgmSyncTaskState.Running,
  BgmSyncTaskState.RetryScheduled,
  BgmSyncTaskState.AwaitingRefetch
]);

const BGM_SYNC_STATE_LABELS: Record<BgmSyncTaskState, string> = {
  Pending: '等待同步',
  Running: '正在同步',
  RetryScheduled: '等待重试',
  AwaitingRefetch: '等待剧集刷新',
  Paused: '自动同步已暂停',
  Blocked: '同步受阻',
  Abandoned: '已放弃同步',
  Exhausted: '重试已用尽',
  Succeeded: '已同步'
};

const BGM_SYNC_OPERATION_LABELS: Record<BgmSyncOperation, string> = {
  Subject: '收藏状态',
  BookProgress: '阅读进度',
  WatchedEpisodes: '已看剧集',
  UnwatchedEpisodes: '未看剧集',
  ContentRefetch: '剧集刷新'
};

export const FAVORITE_LABEL: {[status: string]: string} = {
  [FavoriteStatus.Planned]: '想看',
  [FavoriteStatus.Watched]: '看过',
  [FavoriteStatus.Watching]: '在看',
  [FavoriteStatus.OnHold]: '搁置',
  [FavoriteStatus.Dropped]: '抛弃'
};

@Component({
    selector: 'app-favorite-manager',
    imports: [
    NgClass,
    DatePipe,
    MyReviewComponent,
    ReactiveFormsModule,
    RouterLink
],
    templateUrl: './favorite-manager.component.html',
    styleUrl: './favorite-manager.component.less'
})
export class FavoriteManagerComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();
  private bgmSyncPollSubscription?: Subscription;
  private toastRef: UIToastRef<UIToastComponent>;

  eFavoriteStatus = FavoriteStatus;
  eFavoriteLabel = FAVORITE_LABEL;

  @Input()
  item!: MainItem;

  @Input()
  favorite!: Favorite;

  favProgressForm!: FormGroup;
  isUpdatingProgress: boolean = false;

  epCount!: number;

  isDarkTheme: boolean = false;

  readonly bgmSyncStatus = signal<BgmSyncStatus | null>(null);
  readonly bgmSyncSubmitting = signal(false);
  readonly bgmSyncPolling = signal(false);
  readonly bgmSyncReauthorizationRequired = signal(false);
  readonly bgmSyncActive = computed(() => {
    const status = this.bgmSyncStatus();
    return !!status && ACTIVE_BGM_SYNC_STATES.has(status.state);
  });
  readonly bgmSyncStateLabel = computed(() => {
    const status = this.bgmSyncStatus();
    return status ? BGM_SYNC_STATE_LABELS[status.state] : '';
  });
  readonly bgmSyncOperationLabel = computed(() => {
    const operation = this.bgmSyncStatus()?.operation;
    return operation ? BGM_SYNC_OPERATION_LABELS[operation] : '';
  });
  readonly bgmSyncStatusClass = computed(() => {
    switch (this.bgmSyncStatus()?.state) {
      case BgmSyncTaskState.Succeeded:
        return 'succeeded';
      case BgmSyncTaskState.Blocked:
      case BgmSyncTaskState.Exhausted:
      case BgmSyncTaskState.Abandoned:
        return 'failed';
      case BgmSyncTaskState.Paused:
        return 'paused';
      default:
        return 'active';
    }
  });
  readonly bgmSyncIconClass = computed(() => {
    switch (this.bgmSyncStatus()?.state) {
      case BgmSyncTaskState.Succeeded:
        return 'check circle';
      case BgmSyncTaskState.Blocked:
      case BgmSyncTaskState.Exhausted:
      case BgmSyncTaskState.Abandoned:
        return 'exclamation triangle';
      case BgmSyncTaskState.Paused:
        return 'pause circle';
      default:
        return 'notched circle loading';
    }
  });

  constructor(private miraApiService: DefaultMira,
              private bangumiProviderService: BangumiProviderService,
              private dialog: UIDialog,
              private fb: FormBuilder,
              toast: UIToast) {
    this.toastRef = toast.makeText();
  }

  ngOnDestroy(): void {
    this.bgmSyncPollSubscription?.unsubscribe();
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    if (this.item.subItems && this.item.subItems.length > 0) {
      this.favProgressForm = this.fb.group({
        epFavCount: [0, Validators.required]
      });

      this.epCount = this.item.subItems.filter((subItem) => {
        return subItem.type === EpisodeTypeEnum.Episode;
      }).length;
      this.subscription.add(
        this.miraApiService.getFavoriteProgressByMainItemId(this.item.id, EpisodeTypeEnum.Episode)
          .subscribe({
            next:({progress}) => {
              this.favProgressForm.patchValue({epFavCount: progress});
            },
            error: (error) => {
              console.log(error);
            }}
          )
      );
    }
    this.loadInitialBgmSyncStatus();
  }

  updateProgress(): void {
    const {epFavCount} = this.favProgressForm.value;
    this.isUpdatingProgress = true;
    this.favProgressForm.markAsPristine();
    this.subscription.add(
      this.miraApiService.updateFavoriteProgress(this.item.id, epFavCount, EpisodeTypeEnum.Episode)
        .subscribe({
          next: () => {
            this.isUpdatingProgress = false;
            this.startBgmSyncPolling();
          },
          error: (error) => {
            console.log(error);
            this.isUpdatingProgress = false;
          }
        })
    );
  }

  editReview() {
    const dialogRef = this.dialog.open(EditReviewDialogComponent, {backdrop: true, stickyDialog: true});
    dialogRef.componentInstance.item = this.item;
    if (this.favorite) {
      dialogRef.componentInstance.favorite = Object.assign({}, this.favorite);
    }
    this.subscription.add(dialogRef.afterClosed().pipe(
      switchMap((result) => {
        if (result) {
          // console.log(result);
          if (this.favorite) {
            return this.miraApiService.patchFavorite(String(this.favorite.id), result, "response").pipe(map((resp) => {
              if (resp.status === 200) {
                return {
                  id: this.favorite.id,
                  rating: result.rating,
                  reviewComment: result.reviewComment,
                  status: result.status,
                  item: this.favorite.item
                };
              } else {
                throw new Error(resp && resp.status ?`(HTTP ${resp.status}) ${resp.statusText}` : "Unknown error");
              }
            }));
          } else {
            return this.miraApiService.createFavorite(result);
          }
        } else {
          return of(false);
        }
      }),)
      .subscribe({next: (result) => {
        if (result) {
          this.favorite = result as Favorite;
          this.startBgmSyncPolling();
        }
        // Do nothing
      }, error: (error) => {
        console.log(error);
        this.toastRef.show('更新失败');
      }})
    );
  }

  syncFavoriteToBgm(): void {
    const favoriteId = this.getFavoriteId();
    if (!favoriteId || this.bgmSyncSubmitting() || this.bgmSyncActive()) {
      return;
    }
    this.bgmSyncSubmitting.set(true);
    this.subscription.add(
      this.bangumiProviderService.enqueueBgmSync(favoriteId).pipe(
        tap(() => {
          this.bgmSyncReauthorizationRequired.set(false);
          this.startBgmSyncPolling();
        }),
        finalize(() => {
          this.bgmSyncSubmitting.set(false);
        })
      ).subscribe({
        error: (error: HttpErrorResponse) => {
          if (this.isReauthorizationError(error)) {
            this.bgmSyncReauthorizationRequired.set(true);
            this.toastRef.show('bgm.tv 授权已失效，请重新连接账号');
            return;
          }
          this.toastRef.show(extractMessage(error));
        }
      })
    );
  }

  private loadInitialBgmSyncStatus(): void {
    const favoriteId = this.getFavoriteId();
    if (!favoriteId) {
      return;
    }
    this.subscription.add(
      this.bangumiProviderService.getBgmSyncStatus(favoriteId).pipe(
        catchError(() => EMPTY)
      ).subscribe((status) => {
        this.applyBgmSyncStatus(status);
        if (ACTIVE_BGM_SYNC_STATES.has(status.state)) {
          this.startBgmSyncPolling(BGM_SYNC_POLL_INTERVAL_MS);
        }
      })
    );
  }

  private startBgmSyncPolling(initialDelay = 0): void {
    const favoriteId = this.getFavoriteId();
    if (!favoriteId) {
      return;
    }
    this.bgmSyncPollSubscription?.unsubscribe();
    this.bgmSyncPolling.set(true);
    this.bgmSyncPollSubscription = timer(initialDelay, BGM_SYNC_POLL_INTERVAL_MS).pipe(
      switchMap(() => this.bangumiProviderService.getBgmSyncStatus(favoriteId)),
      tap((status) => this.applyBgmSyncStatus(status)),
      takeWhile((status) => ACTIVE_BGM_SYNC_STATES.has(status.state), true),
      take(BGM_SYNC_MAX_POLLS),
      finalize(() => this.bgmSyncPolling.set(false))
    ).subscribe({
      error: (error: HttpErrorResponse) => {
        if (this.isReauthorizationError(error)) {
          this.bgmSyncReauthorizationRequired.set(true);
          return;
        }
        if (error.status !== 404) {
          this.toastRef.show(extractMessage(error));
        }
      }
    });
  }

  private applyBgmSyncStatus(status: BgmSyncStatus): void {
    this.bgmSyncStatus.set(status);
    this.bgmSyncReauthorizationRequired.set(status.reauthorizationRequired);
  }

  private getFavoriteId(): string | null {
    return this.favorite?.id === undefined || this.favorite?.id === null
      ? null
      : String(this.favorite.id);
  }

  private isReauthorizationError(error: HttpErrorResponse): boolean {
    return error.status === 409 || error.error?.message === 'reauthorization_required';
  }
}
